import { Box, Checkbox, FormControlLabel, Stack } from "@mui/material";
import { ControlGroup } from "../views/ControlGroup";
import { numberFormatters } from "../../../../shared/utils/numberFormatters";
import { assetService } from "../../../../../shared/services/AssetService";
import { ApplicationTooltip } from "../../../views";
import { NumericField } from "../../../controls";
import dayjs, { Dayjs } from "dayjs";
import { ContractDetailsState } from "./ContractDetails";
import { OrderState } from "../../../../../shared/types/OrderState";
import { oderManagerUtils } from "../utils/OrderManagerUtils";
import { ApplicationDateTimePicker } from "../../../../../shared/components/date-time-picker/ApplicationDateTimePicker";
import { TargetPlatform } from "../../../../../shared/types/TargetPlatform";
import { TargetPlatformSelector } from "../../../controls";
import { orderArchiveUtils } from "../../../utils/OrderArchiveUtils";
import { mathCalculationService } from "../../../../../shared/services/MathCalculationService";
import { NumericFieldWithStepSelector } from "../../../../portfolio-profile/components/chart-settings-button/NumericFieldWithStepSelector";
import { OrderProcessingType } from "../../../../../shared/types/OrderProcessingType";
import { FeeDetails } from "./FeeDetails";
import { OrderProcessingTypeSelector } from "./OrderProcessingTypeSelector";

const impliedVolatilityStepOptions = [0.1, 0.01, 0.001, 0.0001];
const priceStepOptions = [10000, 5000, 1000, 500, 100];
const sizeStepOptions = [1, 0.1, 0.01, 0.001, 0.0001];

export type OrderDetailsState = {
    externalId: number;
    state: OrderState;
    targetPlatform: TargetPlatform;
    executedAt: Dayjs,
    underlyingPriceAtExecution: number,
    impliedVolatilityAtExecution: number,
    exchangeOrderId: number | null;
    comment: string | null;
    fee: number;
    processingType: OrderProcessingType;
    price: number;
    size: number;
    volume: number;
    delta: number;
    gamma: number;
    vega: number;
    theta: number;
};

type OrderDetailsProps = {
    contractDetails: ContractDetailsState;
    orderDetails: OrderDetailsState;
    handleOrderDetailsChange: (patch: Partial<OrderDetailsState>) => void;
    automaticRatesAvailable: boolean;
    feeCalculationError: boolean;
    onProcessingTypeChange: (value: OrderProcessingType) => void;
    onFeeValidityChange: (valid: boolean) => void;
}

export function OrderDetails({
    contractDetails,
    orderDetails,
    handleOrderDetailsChange,
    automaticRatesAvailable,
    feeCalculationError,
    onProcessingTypeChange,
    onFeeValidityChange,
}: OrderDetailsProps) {

    const handleActiveStateChanging = (newValue: boolean): void => {
        handleOrderDetailsChange({
            state: newValue ? OrderState.Active : OrderState.Disabled,
        });
    }

    const handleArchiveStateChanging = (newValue: boolean): void => {
        handleOrderDetailsChange({
            state: newValue ? OrderState.Archived: OrderState.Active,
        });
    }
    
    const handleOpenedAtChange = (newValue: dayjs.Dayjs | null) => {
        if (newValue === null){
            return;
        }

        handleOrderDetailsChange({executedAt: newValue});
    }

    const handleOpenPriceChanging = (newValue: number): void => {
        handleOrderDetailsChange({
            price: newValue,
            volume: -newValue * orderDetails.size,
        });
    }

    const handleSizeChanging = (newSize: number): void => {
        const roundedSize = mathCalculationService.round(newSize, 4);

        if (assetService.isOptionType(contractDetails.assetType)) {
            handleOrderDetailsChange({
                size: roundedSize,
                volume: -orderDetails.price * roundedSize,
                delta: orderDetails.delta * roundedSize,
                gamma: (orderDetails.gamma ?? 0) * roundedSize,
                vega: (orderDetails.vega ?? 0) * roundedSize,
                theta: (orderDetails.theta ?? 0) * roundedSize,
            });
        }
        else {
            handleOrderDetailsChange({
                size: roundedSize,
                volume: -orderDetails.price * roundedSize,
                delta: roundedSize,
                gamma: 0,
                vega: 0,
                theta: 0,
            });
        }
    }

    const handleUnderlyingPriceAtOpenChange = (newValue: number): void => {
        handleOrderDetailsChange({underlyingPriceAtExecution: newValue});
    }

    const handleImpliedVolatilityAtOpenChange = (newValue: number): void => {
        handleOrderDetailsChange({impliedVolatilityAtExecution: newValue});
    }
    
    return (
        <ControlGroup title="Order details">
            <Stack direction="row" spacing={1}>
                <ApplicationTooltip
                    title="The item is taken into account when calculating portfolio profile"
                    placement={'top-start'}
                    disableInteractive
                    offset={[0, -8]}
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                sx={{
                                    padding: 0,
                                    marginRight: 0.5,
                                    marginLeft: '-3px',
                                    '&.Mui-disabled': {
                                        color: 'rgba(0, 0, 0, 0.25)',
                                    },
                                }}
                                color="default"
                                id="isActive"
                                name="isActive"
                                checked={orderDetails.state === OrderState.Active || orderDetails.state === OrderState.Archived}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleActiveStateChanging(event.target.checked)}
                                disabled={
                                    orderDetails.state === OrderState.Archived ||
                                    oderManagerUtils.isOptionCanBeCancelled(orderDetails)
                                }
                            />
                        }
                        label="Active"
                        sx={{
                            padding: 0,
                            margin: 0,
                        }}
                    />
                </ApplicationTooltip>
                <ApplicationTooltip
                    title={orderArchiveUtils.getArchiveControlTooltip(
                        orderDetails.state, orderDetails.targetPlatform)}
                    placement={'top-start'}
                    disableInteractive
                    offset={[0, -8]}
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                sx={{
                                    padding: 0,
                                    marginRight: 0.5,
                                    '&.Mui-disabled': {
                                        color: 'rgba(0, 0, 0, 0.25)',
                                    },
                                }}
                                color="default"
                                id="archived"
                                name="archived"
                                checked={orderDetails.state === OrderState.Archived}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleArchiveStateChanging(event.target.checked)}
                                disabled={orderArchiveUtils.isArchiveControlDisabled(
                                    orderDetails.state, orderDetails.targetPlatform)}
                            />
                        }
                        label="Archived"
                        sx={{
                            padding: 0,
                            margin: 0
                        }}
                    />
                </ApplicationTooltip>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="flex-start">
                <TargetPlatformSelector
                    value={orderDetails.targetPlatform}
                    onValueChange={(targetPlatform) => handleOrderDetailsChange({targetPlatform})}
                    disabled={oderManagerUtils.isOriginalOrderArchived(orderDetails)}
                />
                <OrderProcessingTypeSelector
                    value={orderDetails.processingType}
                    disabled={oderManagerUtils.isOriginalOrderArchived(orderDetails)}
                    automaticRatesAvailable={automaticRatesAvailable}
                    onValueChange={onProcessingTypeChange}
                />
                <ApplicationDateTimePicker
                    label="Executed at"
                    value={orderDetails.executedAt}
                    onChange={handleOpenedAtChange}
                    tooltipTitle="Date and time the order was executed"
                    tooltipPlacement={'top-start'}
                    disabled={oderManagerUtils.isOriginalOrderArchived(orderDetails)}
                    slotProps={{
                        textField: {
                            sx: {
                                "& .MuiInputBase-input": {
                                    paddingBottom: '2px',
                                },
                            },
                        },
                    }}
                />
                
            </Stack>
            <Stack direction="row" spacing={2}>
                <NumericFieldWithStepSelector
                    id="openPrice"
                    label="Open price"
                    value={orderDetails.price}
                    onValueChange={handleOpenPriceChanging}
                    formatter={(value: number) => numberFormatters.format2(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                    disabled={oderManagerUtils.isOriginalOrderArchived(orderDetails)}
                    initialSelectedStep={100}
                    stepOptions={priceStepOptions}
                    buttonContent="P"
                    tooltipSubject="open price"
                    selectorWidth={70}
                />
                <NumericFieldWithStepSelector
                    id="size"
                    label="Size"
                    value={orderDetails.size}
                    onValueChange={handleSizeChanging}
                    formatter={(value: number) => numberFormatters.format4(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                    numericFieldStep={0.0001}
                    disabled={oderManagerUtils.isOriginalOrderArchived(orderDetails)}
                    initialSelectedStep={0.001}
                    stepOptions={sizeStepOptions}
                    buttonContent="L"
                    tooltipSubject="order size"
                    selectorWidth={75}
                />
                <FeeDetails
                    value={orderDetails.fee}
                    disabled={oderManagerUtils.isOriginalOrderArchived(orderDetails)}
                    processingType={orderDetails.processingType}
                    calculationError={feeCalculationError}
                    onValueChange={fee => handleOrderDetailsChange({ fee })}
                    onValidityChange={onFeeValidityChange}
                />
                <ApplicationTooltip
                    title="Implied volatility value at the time the order was executed"
                    placement={'top-start'}
                    disableInteractive
                    offset={[0, -8]}
                >
                    <Box>
                        <NumericFieldWithStepSelector
                            id="impliedVolatilityAtOpen"
                            label="IV"
                            value={orderDetails.impliedVolatilityAtExecution}
                            onValueChange={handleImpliedVolatilityAtOpenChange}
                            formatter={(value: number) => numberFormatters.format4(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                            disabled={!assetService.isOptionType(
                                    contractDetails.assetType) ||
                                oderManagerUtils.isOriginalOrderArchived(orderDetails)}
                            numericFieldStep={0.0001}
                            initialSelectedStep={0.01}
                            stepOptions={impliedVolatilityStepOptions}
                            buttonContent="V"
                            tooltipSubject="order IV"
                            selectorWidth={75}
                        />
                    </Box>
                </ApplicationTooltip>
                <ApplicationTooltip
                    title="Underlying asset price at the time the order was executed"
                    placement={'top-start'}
                    disableInteractive
                    offset={[0, -8]}
                >
                    <Box>
                        <NumericFieldWithStepSelector
                            id="underlyingPriceAtOpen"
                            label="Underlying price"
                            value={orderDetails.underlyingPriceAtExecution}
                            onValueChange={handleUnderlyingPriceAtOpenChange}
                            formatter={(value: number) => numberFormatters.format0(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                            disabled={!assetService.isOptionType(
                                    contractDetails.assetType) ||
                                oderManagerUtils.isOriginalOrderArchived(orderDetails)}
                            initialSelectedStep={100}
                            stepOptions={priceStepOptions}
                            buttonContent="P"
                            tooltipSubject="order underlying price"
                            selectorWidth={70}
                        />
                    </Box>
                </ApplicationTooltip>
            </Stack>
            <Stack direction="row" spacing={2}>
                <NumericField
                    id="volume"
                    name="volume"
                    label="Volume"
                    value={orderDetails.volume}
                    formatter={(value: number) => numberFormatters.format0(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                    width={70}
                    disabled
                />
                <NumericField
                    id="orderDelta"
                    name="orderDelta"
                    label="Delta (O)"
                    value={orderDetails.delta}
                    formatter={(value: number) => numberFormatters.format2(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                    width={70}
                    disabled
                />
                <NumericField
                    id="orderGamma"
                    name="orderGamma"
                    label="Gamma (O)"
                    value={orderDetails.gamma}
                    formatter={(value: number) => numberFormatters.format6(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                    width={85}
                    disabled
                />
                <NumericField
                    id="orderVega"
                    name="orderVega"
                    label="Vega (O)"
                    value={orderDetails.vega}
                    formatter={(value: number) => numberFormatters.format2(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                    width={65}
                    disabled
                />
                <NumericField
                    id="orderTheta"
                    name="orderTheta"
                    label="Theta (O)"
                    value={orderDetails.theta}
                    formatter={(value: number) => numberFormatters.format2(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                    width={70}
                    disabled
                />
            </Stack>
        </ControlGroup>
    )
}
